import { useParams, Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { trackPage } from "@/lib/track";
import { DEVICES } from "@/lib/devices";
import { useEffect, useState } from "react";
import "@/styles/home.css";

const SITE = "https://99tests.com";
const BY_SLUG = Object.fromEntries(DEVICES.map((d) => [d.slug, d]));
const COUNT_MIN = 5; // only show a real tester-count line once it's credible

const TIMELINE = [
  ["Minute 0", "Pay", "Choose a package, pay by card. Invoice issued at checkout."],
  ["Minute 2", "Submit", "Build link or web URL, test cases in any format, and this device as a target."],
  ["Hour 1", "Dispatch", "Offers go to testers who own this phone. First to accept takes the session."],
  ["Day 1–2", "Sessions", "Timed sessions on the real device. Findings recorded live with media."],
  ["Day 3", "Verify and deliver", "Findings checked, merged, graded. Report emailed as page and PDF."],
];

const GENERAL_FAQ = [
  ["How much does it cost and how fast is the report?", "Fixed price from ₹9,999 with a verified pass/fail report in 3 business days. You pay, upload your test cases, and testers who own the device are on it within the hour — no account or sales call."],
  ["Do you write my test cases?", "No — you bring your own, human- or AI-written, in CSV, Excel, Sheets, Word, Markdown or TestRail/Jira exports. We execute them on the real device and verify the results."],
];

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a href="#faq">Questions</a>
        <a className="btn sm blue" href="/pricing" data-testid="device-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div>
        <a href="/mobile-app-testing-india">Mobile app testing in India</a>
        <a href="/pricing">Pricing</a>
        <a href="/sample-report/">Sample report</a>
      </div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/">Home</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

function NotFound() {
  return (
    <div className="app-main"><div className="wrap"><h1>Device not found</h1>
      <p>We don't have a page for that device yet.</p>
      <p><Link className="btn" to="/mobile-app-testing-india">See mobile app testing in India</Link></p></div></div>
  );
}

export default function Device() {
  const { slug } = useParams();
  const d = BY_SLUG[slug];
  const path = `/test-app-on/${slug}`;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!d) return;
    let on = true;
    api.deviceCounts().then((r) => { if (on) setCount((r.counts || {})[slug] || 0); });
    return () => { on = false; };
  }, [d, slug]);

  const related = d
    ? DEVICES.filter((x) => x.slug !== d.slug)
        .sort((a, b) => (a.brand === d.brand ? -1 : 0) - (b.brand === d.brand ? -1 : 0))
        .slice(0, 6)
    : [];

  const faq = d ? [...d.faq, ...GENERAL_FAQ] : [];
  const jsonld = d
    ? {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "BreadcrumbList", itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
            { "@type": "ListItem", position: 2, name: "Mobile app testing in India", item: SITE + "/mobile-app-testing-india" },
            { "@type": "ListItem", position: 3, name: `Test your app on ${d.model}`, item: SITE + path },
          ] },
          { "@type": "HowTo", name: `How to test your app on the ${d.model}`, step: TIMELINE.map(([, name, text]) => ({ "@type": "HowToStep", name, text })) },
          { "@type": "FAQPage", mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        ],
      }
    : null;

  useSeo({
    title: d ? d.metaTitle : "Device not found",
    description: d ? d.metaDesc : undefined,
    path,
    noindex: !d,
    jsonld,
    image: d ? `/device-images/${d.slug}-og.png` : null,
  });
  useEffect(() => { if (d) trackPage(path); }, [d, path]);

  if (!d) return (<><Header /><NotFound /><Footer /></>);

  const specs = [
    ["Brand", d.brand], ["Launched", String(d.year)], ["Screen", d.screen],
    ["Memory", d.ram], ["OS", `${d.os} · ${d.skin}`], ["Typical price", d.price],
  ];

  return (
    <>
      <Header />
      <main data-testid={`device-${d.slug}`}>
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>{d.brand}</b><span className="dot"></span><b>{d.skin}</b><span className="dot"></span><span>Real device · Indian networks</span></div>
              <h1>Test your app on the <em>{d.model}.</em></h1>
              <p className="lede">Real testers run your test cases on an actual {d.model} — {d.hook} — on their own {d.skin} build and real Jio, Airtel and Vi networks, then send back a verified pass/fail report. From ₹9,999, report in 3 business days.</p>
              {count >= COUNT_MIN && (
                <p className="fact" data-testid="device-tester-count"><b>{count} testers</b> in our pool own a {d.model} and can run your cases on it.</p>
              )}
              <div className="actions">
                <a className="btn blue" href="/pricing" data-testid="device-packages-btn">Run your cases on this phone — from ₹9,999</a>
                <a className="btn ghost" href="/sample-report/" data-testid="device-sample-report-link">Read a sample report</a>
              </div>
            </div>
            <div>
              <img src={`/device-images/${d.slug}-hero.png`} alt={`Illustration of ${d.model} running a 99tests session`}
                loading="eager" fetchpriority="high" width="1600" height="2000" data-testid="device-hero-image"
                style={{ width: "100%", height: "auto", maxWidth: 420, display: "block", margin: "0 auto", borderRadius: "var(--r-l)" }} />
            </div>
          </div>
        </section>

        <section style={{ paddingTop: 0, borderTop: 0 }}>
          <div className="wrap">
            <div className="proof" data-testid="device-specs" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              {specs.map(([k, v], i) => (<div key={i}><h4 style={{ marginBottom: 4 }}>{k}</h4><p>{v}</p></div>))}
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">What to test on this device</span><h2>What breaks on the {d.model}.</h2></div>
            <div className="proof" data-testid="device-quirks">
              {d.quirks.map(([h, p], i) => (<div key={i}><h4>{h}</h4><p>{p}</p></div>))}
            </div>
          </div>
        </section>

        <section>
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Where it matters most</span><h2>Which apps most need the {d.model}.</h2></div>
            <p className="lede">Most valuable for {d.categories}. If your users buy phones like this, a bug here is a bug in production.</p>
            <p>Every session runs with real people across Indian cities on their own Jio, Airtel and Vi connections — not a data centre and not an emulator. We match a tester who actually owns a {d.model} to your job.</p>
          </div>
        </section>

        <section className="band" id="how">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Three business days</span><h2>From payment to report.</h2></div>
            <div className="tl">
              {TIMELINE.map((t, i) => (<div key={i}><b>{t[0]}</b><h4>{t[1]}</h4><p>{t[2]}</p></div>))}
            </div>
          </div>
        </section>

        <section id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="device-faq">
              {faq.map(([q, a], i) => (
                <details className="f" key={i} open={i === 0}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Other devices</span><h2>Test on another phone.</h2></div>
            <div data-testid="device-related" style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {related.map((x) => (
                <a key={x.slug} className="btn ghost" href={`/test-app-on/${x.slug}`}>{x.model}</a>
              ))}
              <a className="btn ghost" href="/mobile-app-testing-india">All mobile app testing</a>
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Ship knowing it works on a {d.model}.</h2>
                <p>Pay, upload your test cases, and a tester who owns this phone is on it within the hour. Verified report in three business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
